import { useEffect, useState } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { Link as RouterLink } from 'react-router-dom';
import { Button } from '@mui/material';
import { sendNotificationResponse } from '../../api/backendApi';
import notificationStore from '../../stores/notificationStore';
import { classifyNotificationFailure, outcomeFromResult } from './notificationResponseErrors';

export { classifyNotificationFailure, outcomeFromResult, parseApiError, shouldRefreshExpiredToken } from './notificationResponseErrors';

export function currentResponseUserId() {
  try {
    return getAuth().currentUser?.uid || '';
  } catch {
    return '';
  }
}

export function useResponseUserId() {
  const [uid, setUid] = useState('');
  useEffect(() => {
    let unsubscribe = () => {};
    try {
      unsubscribe = onAuthStateChanged(getAuth(), (user) => {
        setUid(user?.uid || '');
      });
    } catch {
      unsubscribe = () => {};
    }
    return () => unsubscribe();
  }, []);
  return uid;
}

export function useOwnResponseAttempt(notification) {
  const uid = useResponseUserId();
  useEffect(() => {
    if (!notification?.attemptedByUid || !uid) return;
    if (notification.attemptedByUid !== uid) {
      notificationStore.clearResponseAttempt(notification.id);
    }
  }, [notification?.id, notification?.attemptedByUid, uid]);
}

export async function submitNotificationResponse({ notification, responseKey }) {
  const actionId = notification?.actionId;
  if (!actionId) {
    return {
      status: 'error',
      message: 'This notification cannot be answered',
      kind: 'missing',
      pendingResponseKey: null,
      attemptedByUid: null,
    };
  }
  const uid = currentResponseUserId();
  try {
    const result = await sendNotificationResponse({
      notificationId: actionId,
      responseKey,
    });
    return {
      ...outcomeFromResult(result),
      pendingResponseKey: null,
      attemptedByUid: null,
    };
  } catch (error) {
    const outcome = classifyNotificationFailure(error);
    if (outcome.kind === 'network') {
      return {
        ...outcome,
        pendingResponseKey: responseKey,
        attemptedByUid: uid,
      };
    }
    return {
      ...outcome,
      pendingResponseKey: null,
      attemptedByUid: null,
    };
  }
}

export function applyNotificationOutcome(notificationId, outcome) {
  notificationStore.markAsResponded(notificationId, outcome.status, outcome.message, {
    pendingResponseKey: outcome.pendingResponseKey || null,
    attemptedByUid: outcome.attemptedByUid || null,
    responseKind: outcome.kind || null,
  });
}

export function NotificationResponseRecovery({ notification, onRetry, disabled = false }) {
  useOwnResponseAttempt(notification);
  const uid = useResponseUserId();
  if (notification?.responseKind === 'sign-in') {
    return (
      <Button
        component={RouterLink}
        to="/login"
        size="small"
        variant="text"
        sx={{ textTransform: 'none' }}
      >
        Sign in
      </Button>
    );
  }
  const sameUser = Boolean(notification?.attemptedByUid) && notification.attemptedByUid === uid;
  if (notification?.responseKind === 'network' && notification?.pendingResponseKey && sameUser) {
    return (
      <Button
        size="small"
        variant="text"
        disabled={disabled}
        onClick={(event) => {
          event.stopPropagation();
          onRetry(notification.pendingResponseKey);
        }}
        sx={{ textTransform: 'none' }}
      >
        Retry
      </Button>
    );
  }
  return null;
}
